// Copy one locale's content onto another, for any store whose records hold translations: Record<locale, {...}>.
// Planning is pure; nothing is written until the user approves specific rows.

import { hasTranslationContent as hasContent } from "./languages"

export interface Translatable {
  translations?: Record<string, any> | null
}

// One translatable record; bucket + id is where the write goes back to
export interface SyncSource {
  bucket: string
  id: string
  name: string
  record: Translatable
}

export type ConflictKind =
  | "new"       // target locale is empty on this record
  | "overwrite" // target locale already has content

export interface PlanEntry {
  key: string
  bucket: string
  id: string
  name: string
  conflict: ConflictKind
  preview: string
}

export interface SyncPlan {
  from: string
  to: string
  entries: PlanEntry[]
  skipped: number // records with no content in the source locale
}

// Present on every translation, so they don't count as content
const BOOKKEEPING_KEYS = new Set([
  "locale", "updatedAt", "reviewed", "state", "status",
])

function contentEntries(t: Record<string, any> | null | undefined): [string, string][] {
  if (!t || typeof t !== "object") return []
  return Object.entries(t)
    .filter(([k, v]) => !BOOKKEEPING_KEYS.has(k) && typeof v === "string" && v.trim() !== "")
    .map(([k, v]) => [k, v] as [string, string])
}

// One-line preview: strips HTML and collapses whitespace
function snippet(text: string, max = 80): string {
  const flat = text.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat
}

export function planLocaleCopy(sources: SyncSource[], from: string, to: string): SyncPlan {
  const entries: PlanEntry[] = []
  let skipped = 0

  for (const s of sources) {
    const src = s.record.translations?.[from]
    if (!hasContent(src)) { skipped++; continue }

    const fields = contentEntries(src)
    // Preview the name/title if there is one
    const lead = fields.find(([k]) => k === "name" || k === "title") ?? fields[0]

    entries.push({
      key: `${s.bucket}:${s.id}`,
      bucket: s.bucket,
      id: s.id,
      name: s.name,
      conflict: hasContent(s.record.translations?.[to]) ? "overwrite" : "new",
      preview: lead ? snippet(lead[1]) : "",
    })
  }

  return { from, to, entries, skipped }
}

export interface PendingWrite {
  bucket: string
  id: string
  translation: Record<string, any>
}

export interface CopyResult {
  writes: Map<string, PendingWrite> // keyed by bucket:id
  created: number
  overwritten: number
}

// Builds the translations for the approved rows. Copies are forced unreviewed and draft so nothing unread goes live
export function buildLocaleCopy(
  sources: SyncSource[],
  plan: SyncPlan,
  approvedKeys: Iterable<string>,
): CopyResult {
  const approved = new Set(approvedKeys)
  const byKey = new Map(sources.map(s => [`${s.bucket}:${s.id}`, s]))
  const writes = new Map<string, PendingWrite>()
  let created = 0
  let overwritten = 0

  for (const entry of plan.entries) {
    if (!approved.has(entry.key)) continue
    const source = byKey.get(entry.key)
    const src = source?.record.translations?.[plan.from]
    // Source may have been cleared since planning
    if (!src || !hasContent(src)) continue

    writes.set(entry.key, {
      bucket: entry.bucket,
      id: entry.id,
      translation: {
        ...src,
        locale: plan.to,
        reviewed: false,
        // Only hold back publish state on stores that have one
        ...(src.state !== undefined ? { state: "draft" } : {}),
        ...(src.status !== undefined ? { status: "draft" } : {}),
        updatedAt: Date.now(),
      },
    })
    if (entry.conflict === "overwrite") overwritten++
    else created++
  }

  return { writes, created, overwritten }
}

export function localeCopyBlocker(from: string, to: string): string | null {
  if (!from) return "Choose a locale to copy from."
  if (!to) return "Choose a locale to copy to."
  if (from === to) return `${from} and the target are the same locale.`
  return null
}
